import { useEffect, useState } from 'react';
import { Check, CreditCard, ExternalLink, Loader2 } from 'lucide-react';
import { getCardsToRun, markCharged } from '@generated/server/contracts';
import { getStaffPass } from '@generated/hooks/staffPass';
import { money } from '@generated/components/pricing';
import { MONTHS } from '@generated/components/dealCatalog';

type Row = Awaited<ReturnType<typeof getCardsToRun>>['rows'][number];
const monthOpts = () => { const ny = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })); return Array.from({ length: 6 }, (_, i) => { const x = new Date(ny.getFullYear(), ny.getMonth() - 2 + i, 1); return `${MONTHS[x.getMonth()]} ${x.getFullYear()}`; }); };

/** Monthly checklist of Card-on-File customers: run each in Stripe, tick Charged (marks the ad Paid). */
export function CardsToRun() {
  const opts = monthOpts();
  const [month, setMonth] = useState(opts[2] ?? '');
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let on = true; setLoading(true); setError(null);
    getCardsToRun({ data: { pass: getStaffPass(), month } }).then((r) => { if (on) setRows(r?.rows ?? []); })
      .catch((e) => { console.error(e); if (on) setError('Could not load the card list.'); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [month]);

  const toggle = async (r: Row) => {
    const prev = rows; setRows((p) => p.map((x) => (x.id === r.id ? { ...x, paid: !x.paid } : x)));
    try { await markCharged({ data: { pass: getStaffPass(), id: r.id, paid: !r.paid } }); }
    catch (e) { console.error(e); setRows(prev); setError('Could not save that. Please try again.'); }
  };
  const due = rows.filter((r) => !r.paid).reduce((s, r) => s + r.cost, 0);

  return (
    <section className="border-2 border-foreground bg-card p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-[family-name:var(--font-heading)] text-xl font-semibold"><CreditCard className="h-5 w-5" />Cards to run</h3>
          <p className="text-xs text-muted-foreground">Card-on-file customers for this issue. Charge each one in Stripe, then tick Charged. Their ad is marked Paid.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select aria-label="Issue month" value={month} onChange={(e) => setMonth(e.target.value)} className="min-h-11 border border-foreground bg-card px-3 text-sm font-semibold">{opts.map((m) => <option key={m}>{m}</option>)}</select>
          <a href="https://dashboard.stripe.com/customers" target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-1.5 border-2 border-foreground px-3 text-sm font-semibold"><ExternalLink className="h-4 w-4" />Open Stripe</a>
        </div>
      </div>
      {error && <p role="alert" className="mt-3 text-sm text-destructive">{error}</p>}
      {loading ? <div className="mt-3 space-y-2">{[0, 1].map((i) => <div key={i} className="h-5 animate-pulse bg-muted" />)}</div>
        : !rows.length ? <p className="mt-3 text-sm italic text-muted-foreground">No card-on-file ads for {month}. Set a contract's "How they pay" to Card on file to see it here.</p> : (<>
          <ul className="mt-3 divide-y divide-border">{rows.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 py-2 text-sm">
              <div className="min-w-0"><div className="font-medium">{r.name}</div><div className="text-xs text-muted-foreground">{[r.paper === 'The Village Star-Revue' ? 'Village' : 'Red Hook', r.adSize, r.contact, r.email].filter(Boolean).join(' · ')}</div></div>
              <div className="flex items-center gap-3"><span className="font-semibold tabular-nums">{money(r.cost)}</span>
                <button type="button" onClick={() => void toggle(r)} aria-pressed={r.paid} className={`inline-flex min-h-11 items-center gap-1.5 border-2 px-3 text-xs font-semibold ${r.paid ? 'border-foreground bg-foreground text-background' : 'border-foreground'}`}>{r.paid ? <><Check className="h-4 w-4" />Charged</> : 'Mark charged'}</button></div>
            </li>))}</ul>
          <p className="mt-2 text-sm font-semibold">Still to charge: {money(due)}</p>
        </>)}
      {loading && rows.length > 0 && <Loader2 className="mt-2 h-4 w-4 animate-spin" />}
    </section>
  );
}
